import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../components/common/Button';
import { ConfirmationModal } from '../components/common/ConfirmationModal';
import { ContentCard } from '../components/common/ContentCard';
import { ContentPage } from '../components/common/ContentPage';
import { Input } from '../components/common/Input';
import { ResumeListItemCard } from '../components/resume/ResumeListItemCard';
import { useCreateResume, useDeleteResume, useGetAllResumes } from '../services/resume/hooks';
import { ResumeListItem } from '../types/resume';

export const ResumeListPage: React.FC = () => {
    const { data: resumes, isLoading } = useGetAllResumes();
    const navigate = useNavigate();
    const createResumeMutation = useCreateResume();
    const deleteResumeMutation = useDeleteResume();

    const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
    const [resumeToDeleteId, setResumeToDeleteId] = React.useState<number | null>(null);

    const [newResumeName, setNewResumeName] = React.useState('');

    const sortedResumes = resumes?.slice().sort((a: ResumeListItem, b: ResumeListItem) => {
        if (a.isActive) return -1;
        if (b.isActive) return 1;
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

    const handleCreateSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!newResumeName.trim()) return;

        createResumeMutation.mutate(newResumeName, {
            onSuccess: () => {
                setNewResumeName('');
            },
        });
    };

    const handleEdit = (resumeId: number) => {
        navigate(`/resume/edit/${resumeId}`);
    };

    const handleDelete = (resumeId: number) => {
        setResumeToDeleteId(resumeId);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = () => {
        if (resumeToDeleteId !== null) {
            deleteResumeMutation.mutate(resumeToDeleteId, {
                onSuccess: () => {
                    setIsDeleteModalOpen(false);
                    setResumeToDeleteId(null);
                },
            });
        }
    };

    const handleCancelDelete = () => {
        setIsDeleteModalOpen(false);
        setResumeToDeleteId(null);
    };

    if (isLoading) return <p className="text-center p-8">Loading resumes...</p>;

    return (
        <ContentPage className="max-w-4xl">
            <ContentCard>
                <form onSubmit={handleCreateSubmit} className="flex flex-col gap-2">
                    <h2 className="text-lg font-semibold mb-2 text-text-primary">
                        Create new resume
                    </h2>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <div className="flex-1">
                            <Input
                                type="text"
                                placeholder="Resume name"
                                value={newResumeName}
                                onChange={(e) => setNewResumeName(e.target.value)}
                                required
                            />
                        </div>
                        <Button type={'primary'} className="h-[42px]">
                            Create
                        </Button>
                    </div>
                </form>
            </ContentCard>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {sortedResumes?.map((resume) => (
                    <ResumeListItemCard
                        key={resume.id}
                        resume={resume}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                    />
                ))}
            </div>

            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                title="Delete resume"
                message="Are you sure you want to delete this resume? This action cannot be undone."
                onCancel={handleCancelDelete}
                onConfirm={handleConfirmDelete}
            />
        </ContentPage>
    );
};
